import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

import { BottomSheet } from 'components/BottomSheet'
import { FocusTrap, Popper, Presence, useBreakpoint, useScrollLock } from 'slab'

import { Button } from './Button'
import { MultistepFiltersContext } from './context'
import { Filter } from './Filter'
import { List } from './List'

const SCROLL_TIMEOUT = 300

interface MultistepFiltersProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  activeFilter?: string | null;
  children: React.ReactNode;
  defaultActiveFilter?: string | null;
  defaultEditing?: boolean;
  editing?: boolean;
  onActiveFilterChange?: (filter: string | null) => void;
  onEditingChange?: (editing: boolean) => void;
  zIndex?: number;
}

export function MultistepFilters ({
  activeFilter: activeFilterProp,
  children,
  defaultActiveFilter = null,
  defaultEditing = false,
  editing: editingProp,
  onActiveFilterChange,
  onEditingChange,
  zIndex = 1,
  ...props
}: MultistepFiltersProps) {
  const [activeFilter, setActiveFilter] = useControllableState({
    prop: activeFilterProp,
    defaultProp: defaultActiveFilter,
    onChange: onActiveFilterChange,
  })
  const [editing, setEditing] = useControllableState({
    prop: editingProp,
    defaultProp: defaultEditing,
    onChange: onEditingChange
  })

  const timeout = useRef<ReturnType<typeof setTimeout>>()

  const [contentElement, setContentElement] = useState<HTMLElement | null>(null)
  const [buttonSlot, setButtonSlot] = useState<HTMLElement | null>(null)

  const barRef = useRef<HTMLDivElement | null>(null)
  const filterRef = useRef<HTMLElement | null>(null)

  const contentId = useId()

  const { breakpointIsAtLeast } = useBreakpoint()
  const { lockScroll, unlockScroll } = useScrollLock()

  const isMobile = !breakpointIsAtLeast('xs')

  const contextValue = useMemo(
    () => ({
      activeFilter,
      buttonSlot,
      contentElement,
      contentId,
      editing,
      filterRef,
      setActiveFilter,
      setButtonSlot,
      setEditing
    }),
    [buttonSlot, contentElement, contentId, activeFilter, editing, setEditing]
  )

  useEffect(() => {
    if (editing) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [editing])

  useEffect(() => {
    if (!editing) return

    function handleKeyDown (event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (activeFilter) {
          setActiveFilter(null)
        } else {
          setEditing(false)
        }
      }
    }

    function handleClickOutside (event: MouseEvent) {
      const target = event.target as HTMLElement

      // Don't exit `editing` state if user clicks on the filters bar or in
      // the popover.
      if (barRef.current?.contains(target) || contentElement?.contains(target)) return

      setEditing(false)
    }

    function handleFocusIn (event: FocusEvent) {
      const target = event.target as HTMLElement

      // Don't exit `editing` state if focus is within the filters bar or
      // the popover.
      if (barRef.current?.contains(target) || contentElement?.contains(target)) return

      setEditing(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('focusin', handleFocusIn)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [activeFilter, contentElement, editing])

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  return (
    <div {...props} className='relative' style={{ zIndex }}>
      <div
        className={clsx(
          'fixed top-0 left-0 w-full h-full transition duration-300',
          {
            'bg-gray-800/40 backdrop-blur-0':
              editing && !isMobile,
            'bg-transparent pointer-events-none': !editing,
          }
        )}
      />
      <Popper>
        <MultistepFiltersContext.Provider value={contextValue}>
          <Popper.Anchor
            className={clsx(
              'relative flex justify-between items-stretch rounded-lg',
              {
                'bg-white shadow-default shadow-violet-300/50': !editing,
                'bg-gray-100 shadow-default shadow-gray-800/20': editing,
              }
            )}
            ref={barRef}
          >
            {children}
            <div
              className={clsx(
                '!absolute pointer-events-none z-[1] top-0 left-0 h-full w-full rounded-lg',
                {
                  'border-emphasis': !editing,
                  'border-gradient-to-r from-gray-200 to-gray-200': editing,
                }
              )}
            />
          </Popper.Anchor>
          {/**
             * For viewports at or above the `xs` breakpoint, the filter content
             * is rendered in a popover. This component owns the popver
             * container, while the `<MultistepFilters.Filter>` component owns
             * its content and uses a portal to render the content into the
             * popover container.
             *
             * Below the `xs` breakpoint, the filter content is rendered in a
             * bottom sheet. This component owns the bottom sheet container,
             * while the `<MultistepFilters.Filter>` component owns its content
             * and uses a portal to render the content into the bottom sheet
             * container.
             */}
          {!isMobile
            ? (
              <Popper.Portal>
                <Presence asChild present={editing && !!activeFilter}>
                  <Popper.Content
                    asChild
                    className='w-[--popper-anchor-width] max-h-[--popper-available-height] data-[state=closed]:opacity-0 border border-gray-200 bg-white rounded-lg shadow-md outline outline-2 outline-transparent focus-visible:outline-thatch-500 data-[side=top]:animate-slide-up-and-fade-in data-[side=bottom]:animate-slide-down-and-fade-in z-[1] overflow-y-auto overscroll-none p-4'
                    collisionPadding={16}
                    id={contentId}
                    role='dialog'
                    side='bottom'
                    sideOffset={8}
                    zIndex={zIndex}
                  >
                    <FocusTrap
                      active={editing && !!activeFilter}
                      ref={setContentElement}
                    />
                  </Popper.Content>
                </Presence>
              </Popper.Portal>
              )
            : (
              <BottomSheet
                open={editing && !!activeFilter}
                onOpenChange={(open) => {
                  setEditing(open)

                  if (isMobile) {
                    // Delay the scroll lock/unlock to allow the filter bar
                    // to scroll into view first.
                    timeout.current = setTimeout(() => {
                      if (open) {
                        lockScroll()
                      } else {
                        unlockScroll()
                      }
                    }, SCROLL_TIMEOUT)
                  }
                }}
                ref={setContentElement}
                zIndex={zIndex}
              />
              )}
        </MultistepFiltersContext.Provider>
      </Popper>
    </div>
  )
}

MultistepFilters.Button = Button
MultistepFilters.List = List
MultistepFilters.Filter = Filter
